import { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { MonthPeriodNavigator } from "components/designV2/periodNavigator/monthPeriodNavigator.component";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import {
  Container,
  Header,
  Title,
  Controls,
  QuickButtons,
  DatePickerWrapper,
  CardsGrid,
  DragHandle,
  StatCard,
  CardLabel,
  CardValue,
  CardLoader,
  CardComparison,
  ManageLink,
} from "./analytics.component.styles";
import { Api } from "services/api/api.service";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import { formatIsoDate } from "web-apps/company/components/schedule/utils/schedule.dates";
import { getCostsTimeReports } from "web-apps/company/utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import { AnalyticsChart } from "./analytics.chart.component";
import {
  type CustomCost,
  loadCustomCosts,
  getCustomCostsTotal,
  CustomCostsPane,
} from "./analytics.customCosts";
import {
  type CustomSale,
  loadCustomSales,
  getCustomSalesTotal,
  CustomSalesPane,
} from "./analytics.customSales";
import { CsvImportPane } from "./analytics.csvImportPane";
import {
  type CompanyTipTotal,
  fetchCompanyTips,
  getCompanyTipsTotal,
} from "./analytics.companyTips";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  DEFAULT_OPERATING_HOURS,
  loadOperatingHours,
  OperatingHoursPane,
} from "./analytics.operatingHours";
import {
  type PreviousPeriodValues,
  computeBusinessHourGross,
  CARD_ORDER_STORAGE_KEY,
  initCardOrder,
  buildStatCards,
  sortStatCards,
  reorderCards,
  getDateRange,
  getPreviousPeriodDate,
  filterOrdersForPeriod,
  toYearMonth,
  isOvernightActive,
} from "./analytics.component.logic";

/**
 * Everything about a period's personnel cost that comes from the server, and
 * therefore nothing that depends on operating hours. Kept separate from the
 * derived cost so that adjusting opening hours is a local recomputation
 * rather than a refetch.
 */
interface PersonnelCostBasis {
  reports: TimeReportDto[];
  totalCost: number | null;
  grossSalary: number;
}

/**
 * Session cache of time reports, keyed by company + year-month.
 *
 * That's the natural caching boundary: getAllTimeReports fetches a whole
 * month and the response is identical regardless of which period, date, or
 * operating hours prompted it.
 */
const timeReportMonthCache = new Map<
  string,
  { reports: TimeReportDto[]; fetchedAt: number }
>();
const MONTH_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * `allowCached` encodes the asymmetry between the two periods: the current
 * period is what the headline numbers show and always revalidates, while the
 * previous period only backs a comparison line and tolerates a few minutes of
 * staleness. Every fetch — cached or not — refreshes the entry, so navigating
 * to a period always leaves the cache holding freshly-read data.
 */
const fetchTimeReportMonth = async (
  companyId: number,
  yearMonth: number,
  allowCached: boolean,
): Promise<TimeReportDto[]> => {
  const key = `${companyId}|${yearMonth}`;
  if (allowCached) {
    const hit = timeReportMonthCache.get(key);
    if (hit && Date.now() - hit.fetchedAt < MONTH_CACHE_TTL_MS) {
      return hit.reports;
    }
  }
  const res = await Api().company.timeReport.getAllTimeReports(
    companyId,
    yearMonth,
  );
  const reports = res.data.flatMap((w) => w.timeReports);
  timeReportMonthCache.set(key, { reports, fetchedAt: Date.now() });
  return reports;
};

/**
 * Fetches the server-side basis for a period's personnel cost. Takes
 * `overnightActive` rather than the full OperatingHours because only that
 * boolean changes which data has to be fetched — narrowing the dependency so
 * ordinary opening-hour edits don't invalidate the request.
 */
const fetchPersonnelCostBasis = async (
  companyId: number,
  period: AnalyticsPeriod,
  date: LocalDate,
  overnightActive: boolean,
  allowCached: boolean,
): Promise<PersonnelCostBasis> => {
  const { fromDate, toDate } = getDateRange(date, period);
  const apiToDate = overnightActive ? toDate.plusDays(1) : toDate;

  // Build the complete set of year-months covered by this period so we
  // fetch time reports from all relevant months (critical for year view).
  const yearMonths = new Set<number>();
  let ymCursor = fromDate.withDayOfMonth(1);
  while (!ymCursor.isAfter(apiToDate)) {
    yearMonths.add(toYearMonth(ymCursor));
    ymCursor = ymCursor.plusMonths(1);
  }

  const months = await Promise.all(
    Array.from(yearMonths).map((ym) =>
      fetchTimeReportMonth(companyId, ym, allowCached),
    ),
  );
  const allReports = months.flat();

  const filtered = allReports.filter((r) => {
    if (!r.startTime) return false;
    const reportDate = LocalDate.parse(r.startTime.substring(0, 10));
    // Use apiToDate so reports that start on the extra calendar day fetched
    // for overnight windows (e.g. June 1 01:30 belonging to May 31's business
    // window) are not dropped here.
    return !reportDate.isBefore(fromDate) && !reportDate.isAfter(apiToDate);
  });

  if (filtered.length === 0) {
    return { reports: [], totalCost: null, grossSalary: 0 };
  }

  const costs = await getCostsTimeReports(filtered, companyId);
  return {
    reports: filtered,
    totalCost: typeof costs?.totalCost === "number" ? costs.totalCost : null,
    grossSalary: typeof costs?.grossSalary === "number" ? costs.grossSalary : 0,
  };
};

/**
 * Derives the personnel cost from an already-fetched basis. Pure and local,
 * so it re-runs on an operating-hours change without touching the network.
 */
const derivePersonnelCost = (
  basis: PersonnelCostBasis | null,
  period: AnalyticsPeriod,
  date: LocalDate,
  operatingHours: OperatingHours,
): number | null => {
  if (!basis) return null;
  if (basis.reports.length === 0) return 0;
  const { reports, totalCost, grossSalary } = basis;
  if (totalCost !== null && grossSalary > 0) {
    // Scale the business-hour gross by the same ratio the API uses to
    // convert gross salary to total cost (employer contributions etc.).
    const multiplier = totalCost / grossSalary;
    return (
      computeBusinessHourGross(reports, period, date, operatingHours) *
      multiplier
    );
  }
  return totalCost;
};

export const Analytics = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;

  const [period, setPeriod] = useState<AnalyticsPeriod>("month");
  const [selectedDate, setSelectedDate] = useState<LocalDate>(LocalDate.now());
  const [summary, setSummary] =
    useState<GetTimeReportsV2SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [personnelCostLoading, setPersonnelCostLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  // Raw server responses. Filtering and cost derivation happen in memos below,
  // so an operating-hours change recomputes without refetching.
  const [ordersResponse, setOrdersResponse] = useState<Order[] | null>(null);
  const [costBasis, setCostBasis] = useState<PersonnelCostBasis | null>(null);
  const [cardOrder, setCardOrder] = useState<string[]>(initCardOrder);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(
    DEFAULT_OPERATING_HOURS,
  );
  const [editingHours, setEditingHours] = useState(false);
  const [customCosts, setCustomCosts] = useState<CustomCost[]>([]);
  const [editingCustomCosts, setEditingCustomCosts] = useState(false);
  const [customSales, setCustomSales] = useState<CustomSale[]>([]);
  const [editingCustomSales, setEditingCustomSales] = useState(false);
  const [editingCsvImport, setEditingCsvImport] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Previous-period figures backing the "vs previous" comparison line. These
  // load independently of the main numbers — a slow comparison fetch must not
  // hold up the headline values.
  const [prevOrdersResponse, setPrevOrdersResponse] = useState<Order[] | null>(
    null,
  );
  const [prevCostBasis, setPrevCostBasis] = useState<PersonnelCostBasis | null>(
    null,
  );
  const [prevSummary, setPrevSummary] =
    useState<GetTimeReportsV2SummaryResponse | null>(null);
  const [companyTips, setCompanyTips] = useState<CompanyTipTotal[]>([]);
  const [prevCompanyTips, setPrevCompanyTips] = useState<
    CompanyTipTotal[] | null
  >(null);

  // Analytics settings (operating hours, custom costs, custom sales) live in
  // Firebase Storage per company — fetch them once companyId is known, and
  // again if the user switches companies mid-session.
  useEffect(() => {
    if (!companyId) return;
    loadOperatingHours(companyId)
      .then(({ hours, found }) => {
        setOperatingHours(hours);
        // Only prompt initial setup when nothing was found anywhere —
        // don't reopen the pane just because a fetch races a save.
        if (!found) setEditingHours(true);
      })
      .catch(() => setEditingHours(true));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    loadCustomCosts(companyId)
      .then(setCustomCosts)
      .catch(() => setCustomCosts([]));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    loadCustomSales(companyId)
      .then(setCustomSales)
      .catch(() => setCustomSales([]));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    setSummary(null);
    setPrevSummary(null);
    setSummaryLoading(true);

    const fetchSummary = (date: LocalDate) => {
      const { fromDate, toDate } = getDateRange(date, period);
      return Api().company.timeReports.getTimeReportsV2Summary(
        companyId,
        formatIsoDate(fromDate),
        formatIsoDate(toDate),
      );
    };

    fetchSummary(selectedDate)
      .then((res) => {
        setSummary(res.data);
        setSummaryLoading(false);
      })
      .catch(() => {
        setSummary(null);
        setSummaryLoading(false);
      });

    fetchSummary(getPreviousPeriodDate(selectedDate, period))
      .then((res) => setPrevSummary(res.data))
      .catch(() => setPrevSummary(null));
  }, [companyId, selectedDate, period]);

  // Only whether *any* day runs overnight changes which data must be fetched,
  // so the effects below depend on this boolean rather than the whole hours
  // object — editing an open/close time within the same day recomputes
  // locally instead of refetching.
  const overnightActive = isOvernightActive(
    period,
    selectedDate,
    operatingHours,
  );
  // Independent from `overnightActive` above: for period === "day",
  // isOvernightActive looks at only the given date's weekday, so the
  // previous day can need different overnight padding than the selected
  // day (e.g. overnight hours enabled on Saturday only). Reusing
  // `overnightActive` for the previous-day fetch would silently mis-pad its
  // API range and skew the "vs previous day" comparison. For week/month/
  // year this always equals `overnightActive`, since isOvernightActive
  // there checks "does any day in operatingHours run overnight", which
  // doesn't depend on which date is passed in.
  const prevOvernightActive = isOvernightActive(
    period,
    getPreviousPeriodDate(selectedDate, period),
    operatingHours,
  );

  useEffect(() => {
    if (!companyId) return;
    setCostBasis(null);
    setPrevCostBasis(null);
    setPersonnelCostLoading(true);

    fetchPersonnelCostBasis(
      companyId,
      period,
      selectedDate,
      overnightActive,
      // Headline figure — never served from cache.
      false,
    )
      .then((basis) => {
        setCostBasis(basis);
        setPersonnelCostLoading(false);
      })
      .catch(() => {
        setCostBasis(null);
        setPersonnelCostLoading(false);
      });

    fetchPersonnelCostBasis(
      companyId,
      period,
      getPreviousPeriodDate(selectedDate, period),
      prevOvernightActive,
      // Comparison baseline — a few minutes of staleness is acceptable.
      true,
    )
      .then(setPrevCostBasis)
      .catch(() => setPrevCostBasis(null));
  }, [companyId, selectedDate, period, overnightActive, prevOvernightActive]);

  useEffect(() => {
    if (!companyId) return;
    setOrdersResponse(null);
    setPrevOrdersResponse(null);
    setOrdersLoading(true);

    const fetchOrders = (date: LocalDate, overnight: boolean) => {
      const { fromDate, toDate } = getDateRange(date, period);
      const apiToDate = overnight ? toDate.plusDays(1) : toDate;
      return Api().analytics.getOrders(
        companyId,
        fromDate.toString(),
        apiToDate.toString(),
      );
    };

    fetchOrders(selectedDate, overnightActive)
      .then((res) => {
        setOrdersResponse(res.data);
        setOrdersLoading(false);
      })
      .catch(() => {
        setOrdersResponse(null);
        setOrdersLoading(false);
      });

    fetchOrders(
      getPreviousPeriodDate(selectedDate, period),
      prevOvernightActive,
    )
      .then((res) => setPrevOrdersResponse(res.data))
      .catch(() => setPrevOrdersResponse(null));
  }, [companyId, selectedDate, period, overnightActive, prevOvernightActive]);

  // Company tips are keyed to the calendar date the user picked on the Tips
  // page, so they need no overnight adjustment and the plain period range is
  // the right window.
  useEffect(() => {
    if (!companyId) return;
    setCompanyTips([]);
    setPrevCompanyTips(null);

    const loadFor = (date: LocalDate) => {
      const { fromDate, toDate } = getDateRange(date, period);
      return fetchCompanyTips(companyId, fromDate, toDate);
    };

    loadFor(selectedDate)
      .then(setCompanyTips)
      .catch(() => setCompanyTips([]));

    loadFor(getPreviousPeriodDate(selectedDate, period))
      .then(setPrevCompanyTips)
      .catch(() => setPrevCompanyTips(null));
  }, [companyId, selectedDate, period]);

  // ── Local derivations ───────────────────────────────────────────────────────
  // Everything below recomputes from already-fetched data, so operating-hours
  // changes cost nothing in network terms.

  const rawOrders = useMemo(
    () =>
      ordersResponse === null
        ? []
        : filterOrdersForPeriod(
            ordersResponse,
            period,
            selectedDate,
            operatingHours,
          ),
    [ordersResponse, period, selectedDate, operatingHours],
  );

  const sales = useMemo(
    () =>
      ordersResponse === null
        ? null
        : rawOrders.reduce((sum, o) => sum + o.amount, 0),
    [ordersResponse, rawOrders],
  );

  const tips = useMemo(
    () =>
      ordersResponse === null
        ? null
        : rawOrders.reduce((sum, o) => sum + o.tip, 0),
    [ordersResponse, rawOrders],
  );

  const prevOrders = useMemo(
    () =>
      prevOrdersResponse === null
        ? null
        : filterOrdersForPeriod(
            prevOrdersResponse,
            period,
            getPreviousPeriodDate(selectedDate, period),
            operatingHours,
          ),
    [prevOrdersResponse, period, selectedDate, operatingHours],
  );

  const prevSales = useMemo(
    () =>
      prevOrders === null
        ? null
        : prevOrders.reduce((sum, o) => sum + o.amount, 0),
    [prevOrders],
  );

  const prevTips = useMemo(
    () =>
      prevOrders === null
        ? null
        : prevOrders.reduce((sum, o) => sum + o.tip, 0),
    [prevOrders],
  );

  const rawTimeReports = useMemo(() => costBasis?.reports ?? [], [costBasis]);

  const personnelCost = useMemo(
    () => derivePersonnelCost(costBasis, period, selectedDate, operatingHours),
    [costBasis, period, selectedDate, operatingHours],
  );

  const prevPersonnelCost = useMemo(
    () =>
      derivePersonnelCost(
        prevCostBasis,
        period,
        getPreviousPeriodDate(selectedDate, period),
        operatingHours,
      ),
    [prevCostBasis, period, selectedDate, operatingHours],
  );

  // These totals (and the buildStatCards call they feed) only depend on the
  // data below, not on unrelated UI state — memoized so dragging a stat
  // card (which updates dropSlot/dragIndex on every dragover) doesn't
  // re-run this over the full period's custom costs/sales on every tick.
  const orderedStats = useMemo(
    () => {
      const customCostsTotal = getCustomCostsTotal(
        customCosts,
        period,
        selectedDate,
      );
      const customSalesTotal = getCustomSalesTotal(
        customSales,
        period,
        selectedDate,
      );
      // Custom sales (e.g. revenue from sources other than the API) are
      // folded directly into the Sales card total, rather than shown as a
      // separate line like custom earnings — they feed personnelCostPct,
      // salesPerHour, and result the same way API-fetched sales do.
      const combinedSales = sales !== null ? sales + customSalesTotal : null;

      // Tips recorded on the Tips page are folded into the Tips card
      // alongside the tips that arrive on POS orders.
      const companyTipsTotal = getCompanyTipsTotal(companyTips);
      const combinedTips = tips !== null ? tips + companyTipsTotal : null;

      // Custom costs/sales are held entirely in memory, so the previous
      // period's totals need no extra fetch — just re-run the same
      // period-aware totals against the previous period's date.
      const previousDate = getPreviousPeriodDate(selectedDate, period);
      const prevCustomCostsTotal = getCustomCostsTotal(
        customCosts,
        period,
        previousDate,
      );
      const prevCustomSalesTotal = getCustomSalesTotal(
        customSales,
        period,
        previousDate,
      );
      const prevCombinedSales =
        prevSales !== null ? prevSales + prevCustomSalesTotal : null;
      const prevCombinedTips =
        prevTips !== null && prevCompanyTips !== null
          ? prevTips + getCompanyTipsTotal(prevCompanyTips)
          : null;

      // Each field is independently withheld (stays null) until its own
      // fetch has landed, so a card can't briefly compare against a
      // half-loaded previous period and show a wrong delta — see
      // prevCombinedSales/prevCombinedTips above and prevPersonnelCost/
      // prevSummary's own null states. This must NOT be an all-or-nothing
      // gate on the whole object: a single slow/failing fetch (e.g.
      // company tips) would otherwise blank out the comparison on every
      // other card too, not just the one it feeds.
      const previous: PreviousPeriodValues = {
        sales: prevCombinedSales,
        tips: prevCombinedTips,
        personnelCost: prevPersonnelCost,
        customCostsTotal: prevCustomCostsTotal,
        summary: prevSummary,
      };

      return sortStatCards(
        buildStatCards({
          labels: {
            sales: t("CompanyAnalytics.Sales"),
            personnelCost: t("CompanyAnalytics.PersonnelCost"),
            customCosts: t("CompanyAnalytics.CustomCosts"),
            personnelCostPct: t("CompanyAnalytics.PersonnelCostPct"),
            plannedHours: t("CompanyAnalytics.PlannedHours"),
            reportedHours: t("CompanyAnalytics.ReportedHours"),
            salesPerHour: t("CompanyAnalytics.SalesPerHour"),
            tips: t("CompanyAnalytics.Tips"),
            result: t("CompanyAnalytics.Result"),
          },
          sales: combinedSales,
          tips: combinedTips,
          personnelCost,
          customCostsTotal,
          summary,
          previous,
          operatingHours,
          period,
          selectedDate,
          ordersLoading,
          personnelCostLoading,
          summaryLoading,
        }),
        cardOrder,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      customCosts,
      customSales,
      period,
      selectedDate,
      sales,
      tips,
      companyTips,
      prevSales,
      prevTips,
      prevCompanyTips,
      personnelCost,
      prevPersonnelCost,
      summary,
      prevSummary,
      operatingHours,
      ordersLoading,
      personnelCostLoading,
      summaryLoading,
      cardOrder,
      t,
    ],
  );

  const handleDragStart = (index: number) => setDragIndex(index);

  const handleDragOver = (e: React.DragEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const slot = e.clientX < rect.left + rect.width / 2 ? index : index + 1;
    if (dropSlot !== slot) setDropSlot(slot);
  };

  const handleGridDragLeave = (e: React.DragEvent<HTMLElement>) => {
    const related = e.relatedTarget instanceof Node ? e.relatedTarget : null;
    if (!e.currentTarget.contains(related)) {
      setDropSlot(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dropSlot === null) {
      setDragIndex(null);
      setDropSlot(null);
      return;
    }
    const newOrder = reorderCards(
      orderedStats.map((s) => s.key),
      dragIndex,
      dropSlot,
    );
    setCardOrder(newOrder);
    localStorage.setItem(CARD_ORDER_STORAGE_KEY, JSON.stringify(newOrder));
    setDragIndex(null);
    setDropSlot(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropSlot(null);
  };

  const periodOptions = [
    { value: "day", label: t("CompanyAnalytics.Day") },
    { value: "week", label: t("CompanyAnalytics.Week") },
    { value: "month", label: t("CompanyAnalytics.Month") },
    { value: "year", label: t("CompanyAnalytics.Year") },
  ];

  // The cards show a bare delta to stay uncluttered; this spells out what it's
  // measured against on hover.
  const comparisonTooltip = t(
    period === "day"
      ? "CompanyAnalytics.VsPreviousDay"
      : period === "week"
        ? "CompanyAnalytics.VsPreviousWeek"
        : period === "month"
          ? "CompanyAnalytics.VsPreviousMonth"
          : "CompanyAnalytics.VsPreviousYear",
  );

  return (
    <Container>
      <Header>
        <Title>{t("CompanyAnalytics.Analytics")}</Title>
        <Controls>
          <SegmentController
            options={periodOptions}
            value={period}
            onChange={(v) => {
              if (v === "day" || v === "week" || v === "month" || v === "year")
                setPeriod(v);
            }}
          />
          {period === "month" ? (
            <MonthPeriodNavigator
              currentDate={selectedDate}
              onChange={setSelectedDate}
              showTodayButton
            />
          ) : (
            <PeriodNavigator
              currentDate={selectedDate}
              mode={period}
              onChange={setSelectedDate}
              showSubLabel
            />
          )}
          <QuickButtons>
            {showDatePicker ? (
              <DatePickerWrapper>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setShowDatePicker(false);
                    }
                  }}
                  size="compact"
                  dateFormat="DD/MM/YYYY"
                />
              </DatePickerWrapper>
            ) : (
              <SecondaryButton
                label={t("CompanyAnalytics.SelectDate")}
                size="small"
                onClick={() => setShowDatePicker(true)}
              />
            )}
          </QuickButtons>
        </Controls>
      </Header>

      <CardsGrid
        onDragLeave={handleGridDragLeave}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {orderedStats.map((stat, index) => (
          <StatCard
            key={stat.label}
            draggable
            $isDragging={dragIndex === index}
            $dropIndicator={
              dropSlot === index
                ? "left"
                : dropSlot === index + 1
                  ? "right"
                  : null
            }
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
          >
            <DragHandle />
            <CardLabel>{stat.label}</CardLabel>
            {stat.isLoading ? (
              <CardLoader>
                <CircularProgress size={24} />
              </CardLoader>
            ) : (
              <>
                <CardValue>{stat.value}</CardValue>
                {stat.comparison && (
                  <CardComparison
                    $sentiment={stat.comparison.sentiment}
                    title={comparisonTooltip}
                  >
                    {stat.comparison.label}
                  </CardComparison>
                )}
              </>
            )}
            {stat.key === "customCosts" && (
              <ManageLink onClick={() => setEditingCustomCosts(true)}>
                {t("CompanyAnalytics.ManageCustomCosts")} →
              </ManageLink>
            )}
            {stat.key === "sales" && (
              <ManageLink onClick={() => setEditingCustomSales(true)}>
                {t("CompanyAnalytics.ManageCustomSales")} →
              </ManageLink>
            )}
          </StatCard>
        ))}
      </CardsGrid>

      <AnalyticsChart
        orders={rawOrders}
        timeReports={rawTimeReports}
        period={period}
        selectedDate={selectedDate}
        operatingHours={operatingHours}
        totalPersonnelCost={personnelCost}
        customCosts={customCosts}
        customSales={customSales}
        companyTips={companyTips}
        loading={ordersLoading || personnelCostLoading}
        onEditHours={() => setEditingHours(true)}
      />

      {companyId && (
        <>
          <OperatingHoursPane
            initial={operatingHours}
            open={editingHours}
            companyId={companyId}
            onSave={(hours) => {
              setOperatingHours(hours);
              setEditingHours(false);
            }}
            onClose={() => setEditingHours(false)}
          />
          <CustomCostsPane
            initial={customCosts}
            open={editingCustomCosts}
            companyId={companyId}
            period={period}
            selectedDate={selectedDate}
            onSave={(costs) => {
              setCustomCosts(costs);
              setEditingCustomCosts(false);
            }}
            onClose={() => setEditingCustomCosts(false)}
          />
          <CustomSalesPane
            initial={customSales}
            open={editingCustomSales}
            companyId={companyId}
            period={period}
            selectedDate={selectedDate}
            onSave={(sales) => {
              setCustomSales(sales);
              setEditingCustomSales(false);
            }}
            onClose={() => setEditingCustomSales(false)}
            onImport={() => {
              setEditingCustomSales(false);
              setEditingCsvImport(true);
            }}
          />
          <CsvImportPane
            open={editingCsvImport}
            companyId={companyId}
            existing={customSales}
            onSave={(sales) => {
              setCustomSales(sales);
              setEditingCsvImport(false);
            }}
            onClose={() => setEditingCsvImport(false)}
          />
        </>
      )}
    </Container>
  );
};
